import { useNavigate } from "react-router-dom";

import {
  Card,
  Space,
  Typography,
  Button,
  Descriptions,
  Popconfirm,
  App,
} from "antd";

import {
  billTypeLabels,
  clientCategoryLabels,
  clientTypeLabels,
  fileTypeLabels,
} from "../../../constant/enum";
import { formatDate } from "../../../utils/dateUtils";
import { archiveFile } from "../api/fileAPI";
import { IFileResponse } from "../type/file";

const { Text } = Typography;

const DetailSection = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => (
  <div style={{ marginBottom: "16px" }}>
    <Text strong>{title}:</Text>
    <div style={{ marginTop: "4px" }}>
      <Text>{content}</Text>
    </div>
  </div>
);

interface FileDetailProps {
  fileData: IFileResponse;
  isMobile: boolean;
  userRole: string;
  canAccess: boolean;
}

export const FileDetail = ({
  fileData,
  isMobile,
  userRole,
  canAccess,
}: FileDetailProps) => {
  const navigate = useNavigate();
  const { message } = App.useApp();

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {isMobile ? (
        <>
          {/* File Information */}
          <Card title="File Information" type="inner">
            <DetailSection
              title="Reference Number"
              content={fileData.refNumber}
            />
            <DetailSection
              title="Branch"
              content={fileData.branch?.branchName ?? "-"}
            />
            <DetailSection
              title="Client Type"
              content={
                clientTypeLabels[fileData.clientType] || fileData.clientType
              }
            />
            {fileData.clientCategory && (
              <>
                <DetailSection
                  title="Client Category"
                  content={clientCategoryLabels[fileData.clientCategory] || "-"}
                />
                <DetailSection
                  title="Client Category Remark"
                  content={fileData.clientCategoryRemark ?? "-"}
                />
              </>
            )}
            <DetailSection
              title="File Type"
              content={fileTypeLabels[fileData.fileType] || fileData.fileType}
            />
            <DetailSection
              title="File Type Remark"
              content={fileData.fileTypeRemark ?? "-"}
            />
            {fileData.billType && (
              <>
                <DetailSection
                  title="Bill Type"
                  content={
                    billTypeLabels[fileData.billType] || fileData.billType
                  }
                />

                <DetailSection
                  title="Bill Type Remark"
                  content={fileData.billTypeRemark ?? "-"}
                />
              </>
            )}
            {fileData.spaPrice && (
              <DetailSection
                title="SPA Price"
                content={`RM ${fileData.spaPrice.toLocaleString()}`}
              />
            )}
            {fileData.propertyAddress && (
              <DetailSection
                title="Property Address"
                content={fileData.propertyAddress}
              />
            )}
            {fileData.fileRemark && (
              <DetailSection
                title="File Remark"
                content={fileData.fileRemark}
              />
            )}
            {fileData.picName && (
              <DetailSection title="PIC Name" content={fileData.picName} />
            )}
            {fileData.completionDate && userRole === "staff" && (
              <DetailSection
                title="Compliance Date"
                content={formatDate(fileData.completionDate)}
              />
            )}
          </Card>

          {fileData.client && (
            <>
              {/* Client Information */}
              <Card title="Client Information" type="inner">
                <DetailSection title="Name" content={fileData.client.name} />
                <DetailSection
                  title="IC Number"
                  content={fileData.client.icNumber}
                />
                <DetailSection
                  title="Phone"
                  content={fileData.client.phoneNumber}
                />
                <DetailSection title="Email" content={fileData.client.email} />
                <DetailSection
                  title="Address"
                  content={fileData.client.address ?? "-"}
                />
              </Card>{" "}
            </>
          )}

          {fileData.purchaser && (
            <>
              {/* Purchaser Information */}
              <Card title="Purchaser Information" type="inner">
                <DetailSection title="Name" content={fileData.purchaser.name} />
                <DetailSection
                  title="IC Number"
                  content={fileData.purchaser.icNumber}
                />
                <DetailSection
                  title="Phone"
                  content={fileData.purchaser.phoneNumber}
                />
                <DetailSection
                  title="Email"
                  content={fileData.purchaser.email}
                />
                <DetailSection
                  title="Address"
                  content={fileData.purchaser.address ?? "-"}
                />
              </Card>{" "}
            </>
          )}

          {fileData.vendor && (
            <>
              {/* Vendor Information */}
              <Card title="Vendor Information" type="inner">
                <DetailSection title="Name" content={fileData.vendor.name} />
                <DetailSection
                  title="IC Number"
                  content={fileData.vendor.icNumber}
                />
                <DetailSection
                  title="Phone"
                  content={fileData.vendor.phoneNumber}
                />
                <DetailSection title="Email" content={fileData.vendor.email} />
                <DetailSection
                  title="Address"
                  content={fileData.vendor.address ?? "-"}
                />
              </Card>{" "}
            </>
          )}

          {fileData.agent && (
            <>
              {/* Agent Information */}
              <Card title="Agent Information" type="inner">
                <DetailSection title="Name" content={fileData.agent.name} />
                <DetailSection
                  title="IC Number"
                  content={fileData.agent.icNumber}
                />
                <DetailSection
                  title="Phone"
                  content={fileData.agent.phoneNumber}
                />
                <DetailSection title="Email" content={fileData.agent.email} />
                <DetailSection
                  title="Address"
                  content={fileData.agent.address ?? "-"}
                />
              </Card>
            </>
          )}
        </>
      ) : (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card title="File Information" type="inner">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Reference Number">
                {fileData.refNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Branch">
                {fileData.branch?.branchName ?? "-"}
              </Descriptions.Item>
              {fileData.clientCategory && (
                <>
                  <Descriptions.Item label="Client Category">
                    {clientCategoryLabels[fileData.clientCategory] ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Client Category Remark">
                    {fileData.clientCategoryRemark ?? "-"}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Client Type">
                {clientTypeLabels[fileData.clientType] || fileData.clientType}
              </Descriptions.Item>
              <Descriptions.Item label="SPA Price">
                RM {fileData.spaPrice?.toLocaleString() ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="File Type">
                {fileTypeLabels[fileData.fileType] || fileData.fileType}
              </Descriptions.Item>
              <Descriptions.Item label="File Type Remark">
                {fileData.fileTypeRemark ?? "-"}
              </Descriptions.Item>
              {fileData.billType && (
                <>
                  <Descriptions.Item label="Bill Type">
                    {billTypeLabels[fileData.billType] ??
                      fileData.billType ??
                      "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bill Type Remark">
                    {fileData.billTypeRemark ?? "-"}
                  </Descriptions.Item>
                </>
              )}
              {fileData.propertyAddress && (
                <Descriptions.Item label="Property Address" span={2}>
                  {fileData.propertyAddress ?? "-"}
                </Descriptions.Item>
              )}
              {fileData.picName &&
              fileData?.completionDate &&
              userRole === "staff" ? (
                <>
                  <Descriptions.Item label="PIC Name">
                    {fileData.picName ?? "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Compliance Date">
                    {formatDate(fileData.completionDate)}
                  </Descriptions.Item>
                </>
              ) : fileData.picName ? (
                <Descriptions.Item label="PIC Name" span={2}>
                  {fileData.picName ?? "-"}
                </Descriptions.Item>
              ) : fileData.completionDate && userRole === "staff" ? (
                <Descriptions.Item label="Compliance Date" span={2}>
                  {formatDate(fileData.completionDate)}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="File Remark" span={2}>
                {fileData.fileRemark ?? "-"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          {fileData.client && (
            <>
              <Card title="Client Information" type="inner">
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Name">
                    {fileData.client.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="IC Number">
                    {fileData.client.icNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {fileData.client.phoneNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {fileData.client.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>
                    {fileData.client.address ?? "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </>
          )}
          {fileData.purchaser && (
            <>
              <Card title="Purchaser Information" type="inner">
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Name">
                    {fileData.purchaser.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="IC Number">
                    {fileData.purchaser.icNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {fileData.purchaser.phoneNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {fileData.purchaser.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>
                    {fileData.purchaser.address ?? "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </>
          )}
          {fileData.vendor && (
            <>
              <Card title="Vendor Information" type="inner">
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Name">
                    {fileData.vendor.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="IC Number">
                    {fileData.vendor.icNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {fileData.vendor.phoneNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {fileData.vendor.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>
                    {fileData.vendor.address ?? "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </>
          )}
          {fileData.agent && (
            <>
              <Card title="Agent Information" type="inner">
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Name">
                    {fileData.agent.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="IC Number">
                    {fileData.agent.icNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {fileData.agent.phoneNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {fileData.agent.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>
                    {fileData.agent.address ?? "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </>
          )}
        </Space>
      )}
      <Space
        size="middle"
        style={{ width: "100%", justifyContent: "flex-end" }}
      >
        {userRole !== "client" && userRole !== "agent" && (
          <>
            <Popconfirm
              title="Are you sure you want to delete this file?"
              description="This action cannot be undone."
              onConfirm={async () => {
                try {
                  const response = await archiveFile(fileData.id);
                  message.success(response.message);
                  navigate(`/files`);
                } catch (error: any) {
                  message.error(error.message);
                }
              }}
              disabled={!canAccess}
            >
              <Button
                type="primary"
                style={{
                  display: "block",
                  marginLeft: "auto",
                  background: "red",
                  boxShadow: "rgba(207, 29, 29, 0.78) 0px 2px 0px",
                }}
              >
                Delete File
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              style={{
                display: "block",
                marginLeft: "auto",
              }}
              disabled={
                fileData.currentStatus?.status === "completed" || !canAccess
              }
              onClick={() => {
                if (
                  fileData.currentStatus?.status !== "completed" &&
                  canAccess
                ) {
                  navigate(`/files/${fileData.id}/edit`, {
                    state: {
                      fileId: fileData.id,
                      displayName: fileData.refNumber,
                    },
                  });
                }
              }}
            >
              Edit File
            </Button>
          </>
        )}
      </Space>
    </Space>
  );
};
